import type { WishlistItem } from '@integration-layer/utils/wishlist'

/**
 * Enum representing different wishlist snackbar actions.
 */
export enum WishlistSnackbarAction {
  addToWishlist = 'addToWishlist',
  removeFromWishlist = 'removeFromWishlist',
  removeAll = 'removeAll',
}

/**
 * Represents a notification for wishlist actions.
 */
type Notification = {
  item: WishlistItem | null
  action: WishlistSnackbarAction
  timeout: ReturnType<typeof setTimeout>
}

/**
 * Callback functions for different wishlist actions.
 */
type ActionsCallback = {
  add?: () => void
  remove?: () => void
  removeAll?: () => void
}

/**
 * Parameters for getting a snackbar message.
 */
type getSnackbarMessageParams = {
  action: WishlistSnackbarAction
  productId?: string
  actionsCb?: ActionsCallback
}

/**
 * Composable for managing wishlist feedback notifications.
 * Provides methods for adding, deleting, and managing notifications.
 *
 * @param {number} [notificationClosingTime=5000] - Time in milliseconds before a notification closes
 * @returns {Object} An object containing wishlist feedback methods and states
 * @property {Ref<Map<string, Notification>>} wishlistFeedbackNotifications - Map of active notifications
 * @property {number} notificationClosingTime - Duration for notification display
 * @property {Function} addNotification - Adds a new notification
 * @property {Function} deleteNotification - Deletes a specific notification
 * @property {Function} getSnackbarMessage - Retrieves the message for a specific action
 * @property {Function} clearWishlistNotifications - Clears all wishlist notifications
 */
export const useWishlistFeedback = (notificationClosingTime = 5000) => {
  const { ts } = useI18n()
  const { loggedIn } = useUserSession()
  const { wishlist, guestWishlist, getCanonicalUrl } = useRouteHelper()

  // Reactive state for wishlist feedback notifications
  const wishlistFeedbackNotifications = useState(
    'WISHLIST_FEEDBACK_NOTIFICATIONS',
    () => new Map<string, Notification>()
  )

  // Computed property to determine the appropriate wishlist route
  const wishlistRoute = computed(() =>
    loggedIn.value ? wishlist : guestWishlist
  )

  /**
   * Adds a new notification to the wishlist feedback notifications.
   *
   * @param {WishlistItem | null} item - The wishlist item associated with the notification
   * @param {WishlistSnackbarAction} action - The type of action performed
   */
  const addNotification = (
    item: WishlistItem | null,
    action: WishlistSnackbarAction
  ) => {
    const productCode = item?.productCode ?? 'all'
    wishlistFeedbackNotifications.value.set(productCode, {
      item,
      action,
      timeout: setTimeout(() => {
        if (wishlistFeedbackNotifications.value.has(productCode)) {
          wishlistFeedbackNotifications.value.delete(productCode)
        }
      }, notificationClosingTime),
    })
  }

  /**
   * Deletes a specific notification by product code.
   *
   * @param {string} productCode - The product code to delete the notification for
   */
  const deleteNotification = (productCode: string) => {
    if (wishlistFeedbackNotifications.value.has(productCode)) {
      clearTimeout(
        wishlistFeedbackNotifications.value.get(productCode)?.timeout
      )
      wishlistFeedbackNotifications.value.delete(productCode)
    }
  }

  /**
   * Retrieves the snackbar message and action for a specific wishlist action.
   *
   * @param {Object} params - Parameters for getting the snackbar message
   * @param {string} [params.productId] - The product ID
   * @param {WishlistSnackbarAction} params.action - The type of action
   * @param {ActionsCallback} [params.actionsCb] - Callback functions for actions
   * @returns {Object} An object containing the message and action
   */
  const getSnackbarMessage = ({
    productId,
    action,
    actionsCb,
  }: getSnackbarMessageParams) => {
    const actions = {
      [WishlistSnackbarAction.addToWishlist]: {
        message: ts('feedback.addedToWishlist'),
        actionMessage: ts('feedback.goToWishlist'),
        action: () => {
          if (productId) deleteNotification(productId)
          navigateTo(getCanonicalUrl(wishlistRoute.value))
          actionsCb?.add?.()
        },
      },
      [WishlistSnackbarAction.removeFromWishlist]: {
        message: ts('feedback.removedFromWishlist'),
        actionMessage: ts('feedback.undo'),
        action: actionsCb?.remove,
      },
      [WishlistSnackbarAction.removeAll]: {
        message: ts('feedback.removeAll'),
        actionMessage: ts('feedback.undo'),
        action: actionsCb?.removeAll,
      },
    }
    return actions[action]
  }

  /**
   * Clears all wishlist notifications before component unmount.
   */
  const clearWishlistNotifications = () =>
    onBeforeUnmount(() => {
      wishlistFeedbackNotifications.value.forEach(notification => {
        clearTimeout(notification.timeout)
      })
      wishlistFeedbackNotifications.value.clear()
    })

  return {
    wishlistFeedbackNotifications,
    notificationClosingTime,
    addNotification,
    deleteNotification,
    getSnackbarMessage,
    clearWishlistNotifications,
  }
}
